/**
 * The day's session as a list of steps (pedagogy spec §6.3.4): what is in each step now (from the
 * plan, the mixed-card counts and the settings), how it stands (from the runner's state), how
 * long it takes, and which one the student is on. Pure, so Home's card and the runner read the
 * same model and the rules are tested without a DOM.
 *
 * Empty steps are hidden. A step that was started, finished or skipped stays in the list even
 * when the plan has since moved on (a re-test answered a minute ago is no longer due, but the
 * step it belonged to is "Klart"), and a started step whose content is gone counts as done.
 */
import { daysBetween } from "@shared/time";
import { NEW_ITEMS, todayKey, type TodaySettings } from "./settings";
import { statusOf, type StepStatus, type TodayRunnerState } from "./session";
import {
  STEP_IDS,
  type MixedCards,
  type StepId,
  type TodayNewTopicLecture,
  type TodayPlanDto,
} from "./types";

/** Minutes per item (spec §6.3.4): cards 25 s, a re-test 1.5 min MC / 4 min numeric / 8 min open. */
const SECONDS_PER_CARD = 25;
const RETEST_MINUTES = { mc: 1.5, numeric: 4, open: 8 } as const;
const MINUTES_PER_NEW_ITEM = 4;
const MINUTES_WORKED_PATH = 15;

export interface TodayStep {
  id: StepId;
  /** How the step stands: pending, started (under way), done or skipped. */
  status: StepStatus;
  /** Cards, re-test problems or new problems; what the step held when it ended once it is over. */
  count: number;
  /** "≈ N min"; null once the step is over. */
  minutes: number | null;
  /** The first step that is neither done nor skipped: the one the student is on. */
  current: boolean;
  /** Cards: the moments the mixed queue draws from. */
  chapters: number[];
  /** Re-tests: every problem is a miss (the title and body say so), else a confirmation too. */
  allMisses: boolean;
  /** Re-tests: whole days since the longest-ago exposure; 0 when unknown. */
  spacedDays: number;
  /** New topic: which one, and whether the student already began on it today. */
  topicId: string | null;
  startedToday: boolean;
  worked: boolean;
  /** New topic: the lecture it prepares or catches up on (P1-3), when the plan names one. */
  lecture: TodayNewTopicLecture | null;
}

export interface StepsInput {
  plan: TodayPlanDto;
  /** `GET /decks` → `mixed`; undefined while it loads or when the server sent none. */
  mixed: MixedCards | undefined;
  settings: TodaySettings;
  runner: TodayRunnerState | null;
}

/** How many cards the warm-up holds: what is due and new, capped by the student's limit. */
export function cardCount(mixed: MixedCards | undefined, settings: TodaySettings): number {
  if (!settings.cards || !mixed) return 0;
  return Math.min(mixed.due + mixed.newToday, settings.cardLimit);
}

/** How many problems the new topic's step holds now; 0 once it is done for today. */
export function newCount(plan: TodayPlanDto, settings: TodaySettings): number {
  const topic = plan.newTopic;
  if (!settings.includeNew || !topic || topic.doneToday) return 0;
  return Math.min(NEW_ITEMS, topic.questionIds.length);
}

function retestMinutes(plan: TodayPlanDto): number {
  const total = plan.retest.items.reduce((sum, item) => sum + RETEST_MINUTES[item.type], 0);
  return Math.max(1, Math.round(total));
}

/** Whole days between the longest-ago exposure of a re-test problem and today. */
function spacedDaysOf(plan: TodayPlanDto): number {
  let days = 0;
  for (const item of plan.retest.items) {
    days = Math.max(days, daysBetween(todayKey(item.lastExposedAt), plan.dayKey));
  }
  return days;
}

function effectiveStatus(recorded: StepStatus, live: number): StepStatus {
  // Started and nothing left to do: the work is finished, whichever page ended it.
  return recorded === "started" && live === 0 ? "done" : recorded;
}

export function buildSteps({ plan, mixed, settings, runner }: StepsInput): TodayStep[] {
  const live: Record<StepId, number> = {
    cards: cardCount(mixed, settings),
    retest: Math.min(plan.retest.items.length, settings.retests),
    new: newCount(plan, settings),
  };
  const topic = plan.newTopic;
  const steps: TodayStep[] = [];
  for (const id of STEP_IDS) {
    const status = effectiveStatus(statusOf(runner, id), live[id]);
    if (status === "pending" && live[id] === 0) continue;
    const over = status === "done" || status === "skipped";
    const recorded = runner?.steps[id]?.count;
    const count = over ? (recorded ?? live[id]) : live[id];
    const minutes = over
      ? null
      : id === "cards"
        ? Math.max(1, Math.ceil((count * SECONDS_PER_CARD) / 60))
        : id === "retest"
          ? retestMinutes(plan)
          : topic?.path === "worked"
            ? MINUTES_WORKED_PATH
            : count * MINUTES_PER_NEW_ITEM;
    // The topic of a new step that began earlier stays the one it began with.
    const topicId = id === "new" ? (runner?.newTopicId ?? topic?.topicId ?? null) : null;
    steps.push({
      id,
      status,
      count,
      minutes,
      current: false,
      chapters: id === "cards" ? (mixed?.chapters ?? []) : [],
      allMisses: id === "retest" && plan.retest.items.every((item) => item.reason === "miss"),
      spacedDays: id === "retest" ? spacedDaysOf(plan) : 0,
      topicId,
      startedToday: id === "new" && topic?.startedToday === true,
      worked: id === "new" && topic?.path === "worked",
      // The lecture belongs to the plan's topic; a step that began on another keeps no tag.
      lecture: id === "new" && topic?.lecture && topicId === topic.topicId ? topic.lecture : null,
    });
  }
  const current = steps.findIndex((step) => step.status === "pending" || step.status === "started");
  return steps.map((step, index) => ({ ...step, current: index === current }));
}

/** Every step is over: done or skipped (and there is at least one). */
export function isFinished(steps: readonly TodayStep[]): boolean {
  return steps.length > 0 && steps.every((s) => s.status === "done" || s.status === "skipped");
}

/** True once the student has begun: some step is under way, done or skipped. */
export function hasBegun(steps: readonly TodayStep[]): boolean {
  return steps.some((step) => step.status !== "pending");
}
